<script lang="ts">
  import type { Translate } from "../i18n";

  const { t, onDismiss }: { t: Translate; onDismiss: () => void } = $props();

  /**
   * The demo is a looping CSS animation, so the reduced-motion preference has
   * to switch it off deliberately — a still frame of the finished approach
   * says the same thing without the movement.
   */
  const still =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  /**
   * The drawn approach. The plane's full track — this curve plus the roll-out
   * down the runway — is the `offset-path` in the stylesheet below; motion
   * paths can only be expressed in CSS.
   */
  const ROUTE = "M 30 34 C 84 22, 116 28, 150 48";
</script>

<section class="overlay" aria-label={t("tutorial.title")}>
  <div class="dialog">
    <h2>{t("tutorial.title")}</h2>

    <!-- Decorative: every rule it shows is written out underneath. -->
    <div class="demo" class:demo--still={still} aria-hidden="true">
      <svg viewBox="0 0 320 130" role="presentation">
        <!-- Two runways the yellow aircraft may not use. -->
        <g class="demo__other">
          <rect x="150" y="76" width="136" height="9" rx="1" fill="#3b82f6" />
          <rect x="150" y="76" width="9" height="9" fill="#93c5fd" />
          <rect x="150" y="102" width="136" height="9" rx="1" fill="#f05252" />
          <rect x="150" y="102" width="9" height="9" fill="#fca5a5" />
        </g>

        <!-- The matching one. -->
        <rect
          class="demo__runway"
          x="150"
          y="44"
          width="136"
          height="9"
          rx="1"
          fill="#facc15"
        />
        <rect class="demo__gate" x="150" y="41" width="9" height="15" />

        <!-- Wiped in left to right, so the dashes read as a route being
             drawn rather than marching along a line that is already there. -->
        <defs>
          <clipPath id="tutorial-reveal">
            <rect class="demo__wipe" x="0" y="0" width="156" height="130" />
          </clipPath>
        </defs>
        <g clip-path="url(#tutorial-reveal)">
          <path class="demo__route" d={ROUTE} />
        </g>

        <g class="demo__pointer">
          <circle r="8" fill="rgb(255 255 255 / 0.25)" />
          <circle r="3.4" fill="#ffffff" />
        </g>

        <g class="demo__plane">
          <path
            d="M 7 0 L -5 5 L -2 0 L -5 -5 Z"
            fill="#facc15"
            stroke="#16202c"
            stroke-width="1.6"
            stroke-linejoin="round"
          />
        </g>
      </svg>
    </div>

    <ul class="tutorial__rules">
      <li>{t("tutorial.colour")}</li>
      <li>{t("tutorial.drag")}</li>
      <li>{t("tutorial.separation")}</li>
      <li>{t("tutorial.symbols")}</li>
    </ul>
    <button type="button" onclick={onDismiss}>{t("tutorial.dismiss")}</button>
  </div>
</section>

<style>
  .demo {
    width: min(20rem, 100%);
    border-radius: 0.2rem;
    background: rgb(120 182 122 / 0.22);
    border: 1px solid var(--panel-border);
    overflow: hidden;
  }

  .demo svg {
    display: block;
    width: 100%;
    height: auto;
  }

  .demo__other {
    opacity: 0.45;
  }

  .demo__wipe {
    transform-box: fill-box;
    transform-origin: 0 0;
    transform: scaleX(0);
    animation: route-wipe 5.5s linear infinite;
  }

  .demo__gate {
    fill: #fff8dc;
    animation: gate-pulse 5.5s linear infinite;
  }

  .demo__route {
    fill: none;
    stroke: #fffdf0;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-dasharray: 8 6;
    animation: route-fade 5.5s linear infinite;
  }

  .demo__pointer {
    opacity: 0;
    offset-path: path("M 30 34 C 84 22, 116 28, 150 48");
    offset-rotate: 0deg;
    animation: pointer-drag 5.5s linear infinite;
  }

  .demo__plane {
    offset-path: path("M 30 34 C 84 22, 116 28, 150 48 L 286 48");
    animation: plane-fly 5.5s linear infinite;
  }

  @keyframes route-wipe {
    0% {
      transform: scaleX(0);
    }
    18%,
    100% {
      transform: scaleX(1);
    }
  }

  @keyframes route-fade {
    0%,
    62% {
      opacity: 1;
    }
    76%,
    100% {
      opacity: 0;
    }
  }

  @keyframes pointer-drag {
    0% {
      offset-distance: 0%;
      opacity: 0.9;
    }
    18% {
      offset-distance: 100%;
      opacity: 0.9;
    }
    24%,
    100% {
      offset-distance: 100%;
      opacity: 0;
    }
  }

  @keyframes plane-fly {
    0%,
    22% {
      offset-distance: 0%;
    }
    64% {
      offset-distance: 100%;
    }
    88% {
      offset-distance: 100%;
      opacity: 1;
    }
    97%,
    100% {
      offset-distance: 100%;
      opacity: 0;
    }
  }

  @keyframes gate-pulse {
    0%,
    14% {
      fill: #fff8dc;
    }
    20% {
      fill: #ffffff;
    }
    34%,
    100% {
      fill: #fff8dc;
    }
  }

  /* Reduced motion: hold the finished approach instead of looping it. */
  .demo--still :global(.demo__route),
  .demo--still :global(.demo__pointer),
  .demo--still :global(.demo__plane),
  .demo--still :global(.demo__gate),
  .demo--still :global(.demo__wipe) {
    animation: none;
  }

  .demo--still .demo__wipe {
    transform: scaleX(1);
  }

  .demo--still .demo__pointer {
    opacity: 0;
  }

  .demo--still .demo__plane {
    offset-distance: 100%;
  }

  .tutorial__rules {
    margin: 0;
    padding-left: 1.1rem;
    text-align: left;
    display: grid;
    gap: 0.45rem;
    max-width: 22rem;
  }

  .tutorial__rules li {
    line-height: 1.35;
  }
</style>
